import React, { useState } from 'react';
import { Box, Breadcrumbs, Link, IconButton, Badge, Chip } from '@mui/material';
import {
  NavigateNext as NavigateNextIcon,
  Home as HomeIcon,
  CreateNewFolder as CreateFolderIcon,
  ExpandLess as ExpandLessIcon,
  Description as DescriptionIcon,
  Assessment as ReportIcon,
  FilterList as FilterIcon,
  FolderShared as FolderSharedIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useDroppable } from '@dnd-kit/core';
import { BreadcrumbContainer, ActionButton } from './styles';
import { BRAND_COLORS, alpha } from '../../styles/theme';
import { FileFilters } from '../Dashboard/types/dashboard.types';
import FileFiltersDialog from './FileFiltersDialog';
import { useTranslation } from '../../i18n';
import type { Crumb } from './navigationModel';
import { FILES_ROOT_PATH, folderUrl } from './folderUrl';

interface FileBreadcrumbsProps {
  /**
   * The trail, resolved from the server's ancestor chain (`crumbRefs`), topmost
   * first and ending with the folder being looked at.
   *
   * It used to be `pathSegments: string[]` sliced out of the open folder's
   * display path. That could only ever describe the caller's own tree, and it
   * carried no ids — which is why an ancestor crumb was not a drop target (D-4c).
   * Every crumb is a real node now.
   */
  crumbs: Crumb[];
  /**
   * True when the open folder belongs to ANOTHER organization.
   *
   * Changes the bar in two ways, both disclosure decisions rather than styling:
   * a "shared with you" chip replaces the Home crumb (there is no path from
   * inside somebody's share to your own root, and offering one would be a
   * navigation that silently changes organization), and the folder names above
   * the share root simply are not in `crumbs` — the server never sent them.
   */
  insideShare?: boolean;
  /** Back to the Shared view. Rendered only when `insideShare`. */
  onBackToShared?: () => void;
  showUploadZone: boolean;
  /**
   * Toggle the upload zone. **Undefined hides the button entirely.**
   *
   * Offered inside a folder another organization shared as well, since #1351:
   * the browser routes that upload to the id-addressed
   * `POST /api/file-objects/{parent_id}/upload`, which runs the same flight
   * ingest as `/api/files/upload`. (It was withheld there from B-4 until then,
   * because that endpoint stored a CSV as a plain file — a flight log that
   * contributed no hours.) No caller passes `undefined` today; the prop stays
   * optional so a future surface that cannot upload at all can hide the button
   * rather than offer an action the API must refuse.
   */
  onToggleUploadZone?: () => void;
  /** Navigate to one crumb's node. */
  onNavigateToCrumb: (crumb: Crumb) => void;
  /**
   * Open the "new folder" dialog. **Undefined hides the button** (STD-029 B-3).
   *
   * The condition is whether the open folder can be *addressed*:
   * `POST /api/file-objects/folders` takes a `parent_id`, and a folder
   * synthesized purely from a flight's display path has no registry row to
   * supply one (R-9). Hiding is the rule for id-keyed affordances — never offer
   * an action the API must then refuse. The root is always addressable
   * (`parent_id: null` = the caller's own root), so this only ever hides inside
   * a legacy synthetic folder.
   */
  onOpenCreateFolder?: () => void;
  onNavigateToDocuments?: () => void;
  onNavigateToReports?: () => void;
  /**
   * Open the "Shared with me" view — everything that crosses this
   * organization's boundary, flights and files alike, and a peer of
   * Documents/Reports rather than a directory (#1242).
   *
   * **One entry point.** There used to be two adjacent ones, "Shared" and
   * "Shared files", partitioned by which grant table was written — a split a
   * user has no way to see, and one that left "where do I look for X?"
   * unanswered. If you are about to add a second door here for a third kind of
   * grant: don't. Add a toggle inside the view.
   *
   * Undefined hides the entry point. That condition is AUTHENTICATION, not
   * admin: the comment here previously said "Undefined for a non-admin", which
   * described #1206 — the bug where one `isAdmin` gated both granting a share
   * and seeing the shared listing, hiding all 48 production flight shares from
   * everyone who was not an admin. The call site was fixed; this sentence was
   * left behind. Corrected in #1205 so it stops teaching the wrong rule.
   */
  onNavigateToShared?: () => void;
  /**
   * During a drag, returns true when this crumb is a valid move destination —
   * gates the accept-highlight.
   *
   * **Every crumb can now answer yes**, which is D-4c closing: the flat
   * projection carried no ancestor ids, so an ancestor crumb could be named and
   * not addressed, and the browser refused the drop rather than sending a
   * `parent_id` it did not have.
   */
  canDropOnCrumb?: (crumb: Crumb) => boolean;
  fileFilters?: FileFilters;
  serialNumbers?: string[];
  onFileFilterChange?: (filters: Partial<FileFilters>) => void;
  onResetFileFilters?: () => void;
}

/**
 * A breadcrumb that doubles as a dnd-kit drop target, so a dragged selection can
 * be moved up to that ancestor. Carries `data: { crumb }`, which
 * `FileBrowser.handleDragEnd` reads to resolve the destination — a node ref, not
 * a path, which is what makes an ANCESTOR crumb a legal destination again
 * (D-4c). Applies an accept-highlight while a valid drag hovers it.
 */
interface DroppableCrumbProps {
  crumb: Crumb;
  index: number;
  canDropOnCrumb?: (crumb: Crumb) => boolean;
  children: React.ReactNode;
}

const DroppableCrumb: React.FC<DroppableCrumbProps> = ({
  crumb,
  index,
  canDropOnCrumb,
  children,
}) => {
  // The dnd-kit id is positional, not the node id: a crumb must be unique among
  // droppables, and a folder card in the grid already registers under its own
  // `object_id`. `handleDragEnd` reads the crumb out of `data` and never parses
  // this string.
  const { setNodeRef, isOver } = useDroppable({
    id: `crumb:${index}`,
    data: { crumb },
  });
  const accept = isOver && (canDropOnCrumb?.(crumb) ?? false);
  return (
    <Box
      ref={setNodeRef}
      sx={{
        display: 'flex',
        alignItems: 'center',
        borderRadius: '4px',
        padding: '2px 4px',
        border: `1px ${accept ? 'dashed' : 'solid'} ${
          accept ? BRAND_COLORS.yellow.main : 'transparent'
        }`,
        backgroundColor: accept ? alpha(BRAND_COLORS.yellow.main, 0.15) : 'transparent',
        transition: 'all 0.15s ease',
      }}
    >
      {children}
    </Box>
  );
};

const FileBreadcrumbs: React.FC<FileBreadcrumbsProps> = ({
  crumbs,
  insideShare = false,
  onBackToShared,
  showUploadZone,
  onNavigateToCrumb,
  onOpenCreateFolder,
  onToggleUploadZone,
  onNavigateToDocuments,
  onNavigateToReports,
  onNavigateToShared,
  canDropOnCrumb,
  fileFilters,
  serialNumbers,
  onFileFilterChange,
  onResetFileFilters,
}) => {
  const { t } = useTranslation();
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);

  // Check if any file filters are active
  const hasActiveFilters =
    (fileFilters?.selectedSerial && fileFilters.selectedSerial !== 'All') ||
    fileFilters?.startDate != null ||
    fileFilters?.endDate != null;

  const handleFilterDialogOpen = () => {
    setFilterDialogOpen(true);
  };

  const handleFilterDialogClose = () => {
    setFilterDialogOpen(false);
  };

  return (
    <BreadcrumbContainer>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
          {/* Inside a share there is no Home crumb, and the chip says why. The
              caller has no path from somebody else's subtree to their own root;
              offering one would be a navigation that silently changes
              organization. The way out is back to the Shared view. */}
          {insideShare && (
            <Chip
              size="small"
              icon={<FolderSharedIcon />}
              label={t('fileManagement.sharedFolderChip')}
              data-testid="shared-folder-chip"
              sx={{ height: 24 }}
            />
          )}
          {crumbs.map((crumb, index) => (
            <DroppableCrumb
              key={crumb.ref.id ?? `root-${index}`}
              crumb={crumb}
              index={index}
              canDropOnCrumb={canDropOnCrumb}
            >
              {/* A real anchor since B-5, so a crumb is copyable, middle-
                  clickable and ⌘/ctrl-clickable into a new tab — the address is
                  the folder's id, so what gets pasted survives a rename and a
                  move. The ROOT crumb's href is `/files` even though its ref
                  carries the real root id: the href is the canonical address,
                  the click still hands the node ref to the handler.

                  `draggable={false}` because an `<a>` is natively draggable and
                  the crumb is a dnd-kit drop target; the browser's own drag
                  would fight the pointer sensor. */}
              <Link
                component="a"
                href={crumb.isRoot ? FILES_ROOT_PATH : folderUrl(crumb.ref.id, crumb.label)}
                draggable={false}
                onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
                  // Modified clicks belong to the browser — that is the whole
                  // point of making this an anchor.
                  if (
                    event.button !== 0 ||
                    event.metaKey ||
                    event.ctrlKey ||
                    event.shiftKey ||
                    event.altKey
                  ) {
                    return;
                  }
                  event.preventDefault();
                  onNavigateToCrumb(crumb);
                }}
                data-testid={crumb.isRoot ? 'crumb-root' : 'crumb'}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {crumb.isRoot ? (
                  <>
                    <HomeIcon fontSize="small" />
                    {t('fileManagement.root')}
                  </>
                ) : (
                  crumb.label
                )}
              </Link>
            </DroppableCrumb>
          ))}
        </Breadcrumbs>

        {/* Filter button with badge */}
        {fileFilters && onFileFilterChange && onResetFileFilters && (
          <IconButton
            onClick={handleFilterDialogOpen}
            data-testid="open-file-filters-btn"
            aria-label={t('filterDialog.fileFilters')}
            sx={{
              color: alpha(BRAND_COLORS.forestGreen.main, 0.7),
              '&:hover': {
                color: BRAND_COLORS.forestGreen.main,
                backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.04),
              },
            }}
          >
            <Badge
              variant="dot"
              invisible={!hasActiveFilters}
              sx={{
                '& .MuiBadge-badge': {
                  backgroundColor: BRAND_COLORS.yellow.main,
                },
              }}
            >
              <FilterIcon />
            </Badge>
          </IconButton>
        )}
      </Box>

      <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
        {insideShare && onBackToShared && (
          <ActionButton
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={onBackToShared}
            data-testid="back-to-shared-btn"
          >
            {t('fileManagement.backToShared')}
          </ActionButton>
        )}
        {onOpenCreateFolder && (
          <ActionButton
            size="small"
            startIcon={<CreateFolderIcon />}
            onClick={onOpenCreateFolder}
            data-testid="new-folder-btn"
          >
            {t('fileManagement.newFolder')}
          </ActionButton>
        )}
        {onToggleUploadZone && (
          <ActionButton
            size="small"
            startIcon={showUploadZone ? <ExpandLessIcon /> : <NavigateNextIcon />}
            onClick={onToggleUploadZone}
            data-testid="toggle-upload-zone-btn"
          >
            {showUploadZone ? t('fileManagement.hideUpload') : t('fileManagement.uploadFiles')}
          </ActionButton>
        )}
        {onNavigateToDocuments && (
          <ActionButton
            size="small"
            startIcon={<DescriptionIcon />}
            onClick={onNavigateToDocuments}
          >
            {t('fileManagement.documents')}
          </ActionButton>
        )}
        {onNavigateToReports && (
          <ActionButton size="small" startIcon={<ReportIcon />} onClick={onNavigateToReports}>
            {t('fileManagement.reports')}
          </ActionButton>
        )}
        {/* #1223 is closed by this being ONE button. The old pair read "Shared"
            and "Shared files", and a user could reasonably read the first as
            "everything shared with me" and conclude their files were missing —
            the data agreed with them, since a shared CSV landed under whichever
            of the two the granter's dialog happened to write to.

            Gated on `canViewShares` (authentication), never on admin.
            Collapsing that into an admin check is #1206 — the bug that hid
            every existing share from everyone who was not an admin. */}
        {onNavigateToShared && (
          <ActionButton
            size="small"
            startIcon={<FolderSharedIcon />}
            onClick={onNavigateToShared}
            data-testid="open-shared-view-btn"
          >
            {t('sharedView.navLabel')}
          </ActionButton>
        )}
      </Box>

      {/* File Filters Dialog */}
      {fileFilters && serialNumbers && onFileFilterChange && onResetFileFilters && (
        <FileFiltersDialog
          open={filterDialogOpen}
          onClose={handleFilterDialogClose}
          fileFilters={fileFilters}
          serialNumbers={serialNumbers}
          onFilterChange={onFileFilterChange}
          onResetFilters={onResetFileFilters}
        />
      )}
    </BreadcrumbContainer>
  );
};

export default FileBreadcrumbs;
